import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import { colors } from '../../styles/theme';
import { useMyBrands } from '../../hooks/useMyBrands';
import { getBrandInitial } from '../../Utils/brandForm';
import { useBrandsModal } from '../BrandsModal/BrandsModalContext';
import {
  SHARE_TEMPLATE_DEFAULT,
  SHARE_TEMPLATE_PERSONALIZED,
  normalizeShareTemplate,
  resolveShareBrandPalette,
} from '../../Map/utils/sharedBranding';
import {
  APPEARANCE_PICKER_SX,
  APPEARANCE_PREVIEW_COLUMN_SX,
  BrandWireframe,
  PrincipalBadge,
  TemplatePreview,
  TemplatePreviewCardSkeleton,
  TerrioDefaultBadge,
  TerrioWireframe,
} from './ShareTemplateWireframes';
import {
  appearanceMenuItemSx,
  appearanceMenuPaperSx,
  appearanceSelectSx,
  appearanceTextColors,
} from './ShareTemplateSection.styles';

const { border: BORDER, primary: TEXT_PRIMARY, muted: TEXT_MUTED } = appearanceTextColors;
const TERRIO_OPTION = SHARE_TEMPLATE_DEFAULT;

function brandCaption() {
  return 'Tu logo, tus colores y tu contacto.';
}

function OptionLabel({ name, isPrincipal, isTerrioDefault }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
      <Typography
        component="span"
        sx={{
          color: TEXT_PRIMARY,
          fontSize: '13px',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          flex: 1,
        }}
      >
        {name}
      </Typography>
      {isTerrioDefault ? <TerrioDefaultBadge /> : null}
      {isPrincipal ? <PrincipalBadge /> : null}
    </Box>
  );
}

/**
 * Con qué apariencia ve la página quien recibe el enlace: Terrio, o una de las
 * identidades del dueño. Sirve para listas, lotes y showrooms CAD.
 *
 * El valor viaja en dos campos que se leen juntos. `share_template` decide si
 * hay marca; `brand_id` decide **cuál**. Elegir la principal manda `null`
 * (el enlace sigue a la principal si más adelante se marca otra). Elegir
 * otra marca fija ese id. El destino del mail de consulta lo resuelve el
 * backend: no se elige acá.
 *
 * @param {{
 *   value: string,
 *   brandId?: string|null,
 *   onChange: (shareTemplate: string, brandId: string|null) => void,
 *   disabled?: boolean,
 *   locked?: boolean,
 *   lockedCaption?: string,
 * }} props
 *   `locked`: espacio ajeno (F3). No se listan las marcas del invitado: se
 *   muestra Terrio o "Identidad del espacio" según `value` / `brandId` de la
 *   config del owner. Sin `onChange` efectivo (solo lectura).
 * @see docs/maps/brands.md
 * @see docs/maps/espacios-invite.md
 */
function ShareTemplateSection({
  value,
  brandId = null,
  onChange,
  disabled = false,
  locked = false,
  lockedCaption,
}) {
  const template = normalizeShareTemplate(value);
  const isPersonalized = template === SHARE_TEMPLATE_PERSONALIZED;

  const { openBrands, subscribeBrandsClose } = useBrandsModal();
  // En espacio ajeno no pedimos las marcas del JWT: la apariencia es del owner.
  const { loading, error, brands, defaultBrand, reload } = useMyBrands({
    enabled: !locked,
  });
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (locked) return undefined;
    return subscribeBrandsClose(reload);
  }, [locked, subscribeBrandsClose, reload]);

  // Sin identidades no se puede guardar personalized (400). El selector muestra
  // Terrio; el estado del diálogo tiene que coincidir para no reenviar eso.
  // En espacio ajeno no aplica: la marca es del owner (API).
  useEffect(() => {
    if (locked || loading || brands.length > 0) return;
    if (!isPersonalized && !brandId) return;
    onChangeRef.current?.(SHARE_TEMPLATE_DEFAULT, null);
  }, [locked, loading, brands.length, isPersonalized, brandId]);

  const selectedBrand = useMemo(() => {
    if (locked || !isPersonalized) return null;
    if (!brandId) return defaultBrand;
    return brands.find((brand) => brand.id === brandId) || defaultBrand;
  }, [locked, isPersonalized, brandId, brands, defaultBrand]);

  if (locked) {
    const lockedPreview = isPersonalized ? (
      <BrandWireframe
        primary={colors.primary}
        logoUrl={null}
        brandInitial="E"
        brandName="Espacio"
      />
    ) : (
      <TerrioWireframe />
    );
    let caption = lockedCaption;
    if (!caption) {
      if (!isPersonalized) caption = 'Vista por defecto de la plataforma.';
      else if (brandId) caption = 'Identidad elegida por el dueño del espacio.';
      else caption = 'Identidad principal del dueño del espacio.';
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box>
          <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
            Apariencia
          </Typography>
          <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', lineHeight: 1.4, mt: 0.35 }}>
            Define el aspecto que verá quien reciba el enlace.
          </Typography>
        </Box>
        <Box sx={APPEARANCE_PICKER_SX}>
          <Box
            sx={{
              ...appearanceSelectSx,
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              minHeight: 40,
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <OptionLabel
              name={isPersonalized ? 'Identidad del espacio' : 'Terrio'}
              isTerrioDefault={!isPersonalized}
            />
          </Box>
          <Box sx={APPEARANCE_PREVIEW_COLUMN_SX}>
            <TemplatePreview preview={lockedPreview} />
            <Typography sx={{ color: TEXT_MUTED, fontSize: '11px', lineHeight: 1.4 }}>
              {caption}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const selectTerrio = () => onChange?.(SHARE_TEMPLATE_DEFAULT, null);

  const selectBrand = (brand) => {
    // La principal no se fija: `null` es "seguí la de ahora", que es el
    // default del backend cuando `personalized` no trae `brand_id`.
    const followsPrincipal = brand.is_default || brand.id === defaultBrand?.id;
    onChange?.(SHARE_TEMPLATE_PERSONALIZED, followsPrincipal ? null : brand.id);
  };

  const selectValue = !isPersonalized || !selectedBrand
    ? TERRIO_OPTION
    : String(selectedBrand.id);

  const handleAppearanceChange = (event) => {
    const next = event.target.value;
    if (next === TERRIO_OPTION) {
      selectTerrio();
      return;
    }
    const brand = brands.find((item) => String(item.id) === String(next));
    if (brand) selectBrand(brand);
  };

  const preview = selectedBrand ? (
    <BrandWireframe
      primary={resolveShareBrandPalette(selectedBrand).primary}
      logoUrl={selectedBrand.logo_url}
      brandInitial={getBrandInitial(selectedBrand.brand_name)}
      brandName={selectedBrand.brand_name}
    />
  ) : (
    <TerrioWireframe />
  );

  const caption = selectedBrand
    ? brandCaption()
    : 'Vista por defecto de la plataforma.';

  const renderCreateBrandRow = () => (
    <Box
      component="button"
      type="button"
      onClick={() => openBrands({ startOnCreate: true })}
      disabled={disabled}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1.15,
        m: 0,
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: '10px',
        border: `1px dashed ${BORDER}`,
        bgcolor: 'transparent',
        color: 'inherit',
        font: 'inherit',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': disabled ? undefined : {
          borderColor: colors.primary,
          bgcolor: 'rgba(72,146,145,0.06)',
        },
      }}
    >
      <PaletteOutlinedIcon sx={{ fontSize: 20, color: TEXT_MUTED, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
          Usar mi identidad
        </Typography>
        <Typography sx={{ color: TEXT_MUTED, fontSize: '11px', lineHeight: 1.35 }}>
          Cargá tu logo, tus colores y tu contacto para compartir con tu identidad.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box>
        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
          Apariencia
        </Typography>
        <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', lineHeight: 1.4, mt: 0.35 }}>
          Define el aspecto que verá quien reciba el enlace.
        </Typography>
      </Box>

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}
        aria-busy={loading ? 'true' : undefined}
      >
          {loading ? (
            <TemplatePreviewCardSkeleton />
          ) : (
            <Box sx={APPEARANCE_PICKER_SX}>
              <Select
                size="small"
                value={selectValue}
                disabled={disabled}
                onChange={handleAppearanceChange}
                renderValue={(value) => {
                  if (value === TERRIO_OPTION) return <OptionLabel name="Terrio" isTerrioDefault />;
                  const brand = brands.find((item) => String(item.id) === String(value));
                  if (!brand) return <OptionLabel name="Terrio" isTerrioDefault />;
                  return (
                    <OptionLabel
                      name={brand.brand_name}
                      isPrincipal={Boolean(brand.is_default)}
                    />
                  );
                }}
                inputProps={{ 'aria-label': 'Apariencia del enlace' }}
                sx={appearanceSelectSx}
                MenuProps={{ PaperProps: { sx: appearanceMenuPaperSx } }}
              >
                <MenuItem value={TERRIO_OPTION} sx={appearanceMenuItemSx}>
                  <OptionLabel name="Terrio" isTerrioDefault />
                </MenuItem>
                {brands.map((brand) => (
                  <MenuItem key={brand.id} value={String(brand.id)} sx={appearanceMenuItemSx}>
                    <OptionLabel
                      name={brand.brand_name}
                      isPrincipal={Boolean(brand.is_default)}
                    />
                  </MenuItem>
                ))}
              </Select>

              <Box sx={APPEARANCE_PREVIEW_COLUMN_SX}>
                <TemplatePreview preview={preview} />
                <Typography
                  sx={{
                    color: TEXT_MUTED,
                    fontSize: '11px',
                    lineHeight: 1.4,
                  }}
                >
                  {caption}
                </Typography>
              </Box>
            </Box>
          )}

          {!loading && brands.length === 0 ? renderCreateBrandRow() : null}
        </Box>

        {/* Que no se puedan leer las marcas no impide compartir con Terrio, así
            que se avisa sin alarmar y con la salida a mano. */}
        {error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography sx={{ color: TEXT_MUTED, fontSize: '11px', lineHeight: 1.4 }}>
              No pudimos cargar tus identidades.
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={reload}
              sx={{
                textTransform: 'none',
                fontSize: '11px',
                fontWeight: 600,
                minWidth: 0,
                p: 0,
                color: colors.primaryLight,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              Reintentar
            </Button>
          </Box>
        ) : null}
    </Box>
  );
}

export default ShareTemplateSection;
